import { createContext, type ReactNode, useContext, useMemo } from 'react';
import type { CurrentUser } from '../api/active/generated/types';
import { useActiveSession } from '../api/active/sessionProvider';
import { createQuoteApi } from './quoteApi';

export type QuotePageRuntime = {
  api: ReturnType<typeof createQuoteApi>;
  identity: {
    id: string;
    primaryDepartmentId: string;
  };
  scopeAuthorization: Pick<CurrentUser, 'dataScopes' | 'permissionDataScopes'>;
  canRead: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canCreateVersion: boolean;
  canPublish: boolean;
  canShare: boolean;
  canExport: boolean;
  canArchive?: boolean;
  canShareInternal?: boolean;
  canReadOrderConfirmation: boolean;
  canUseOrderConfirmationOcr: boolean;
  canConfirmOrder: boolean;
  canReadOperations: boolean;
  recoverUnauthorized(): Promise<void>;
};

const QuotePageRuntimeContext = createContext<QuotePageRuntime | null>(null);

export function QuotePageRuntimeProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: QuotePageRuntime;
}) {
  return (
    <QuotePageRuntimeContext.Provider value={value}>
      {children}
    </QuotePageRuntimeContext.Provider>
  );
}

export function ActiveQuotePageRuntimeProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { state, quoteTransport, refresh, offlineDemoEnabled } =
    useActiveSession();
  const ready = state.status === 'ready' ? state : null;
  const value = useMemo<QuotePageRuntime>(
    () => ({
      api: createQuoteApi(quoteTransport, ready?.csrfToken ?? null),
      identity: {
        id: ready?.user.id ?? '',
        primaryDepartmentId: ready?.user.primaryDepartmentId ?? '',
      },
      scopeAuthorization: {
        dataScopes: ready?.user.dataScopes ?? [],
        permissionDataScopes: ready?.user.permissionDataScopes,
      },
      canRead: ready?.user.permissions.includes('quote.read') ?? false,
      canCreate: ready?.user.permissions.includes('quote.create') ?? false,
      canEdit: ready?.user.permissions.includes('quote.edit') ?? false,
      canCreateVersion:
        ready?.user.permissions.includes('quote.version.create') ?? false,
      canPublish: ready?.user.permissions.includes('quote.publish') ?? false,
      canShare:
        ready?.user.permissions.includes('quote.share.external') ?? false,
      canExport: ready?.user.permissions.includes('quote.export') ?? false,
      canArchive: ready?.user.permissions.includes('quote.archive') ?? false,
      canShareInternal:
        ready?.user.permissions.includes('quote.share.internal') ?? false,
      canReadOrderConfirmation:
        ready?.user.permissions.includes('quote.order_confirmation.read') ??
        false,
      canUseOrderConfirmationOcr:
        ready?.user.permissions.includes('quote.order_confirmation.ocr') ??
        false,
      canConfirmOrder:
        ready?.user.permissions.includes('quote.order_confirmation.confirm') ??
        false,
      canReadOperations:
        ready?.user.permissions.some((code) =>
          [
            'operation.read',
            'quote.order_confirmation.confirm',
            'quote.order_confirmation.ocr',
          ].includes(code),
        ) ?? false,
      recoverUnauthorized: refresh,
    }),
    [ready, refresh, quoteTransport],
  );
  if (!ready) return null;
  if (offlineDemoEnabled) return <>{children}</>;
  return (
    <QuotePageRuntimeProvider value={value}>
      {children}
    </QuotePageRuntimeProvider>
  );
}

export function useQuotePageRuntime(): QuotePageRuntime {
  const value = useContext(QuotePageRuntimeContext);
  if (!value) throw new Error('QuotePageRuntimeProvider is required');
  return value;
}

export function useOptionalQuotePageRuntime(): QuotePageRuntime | null {
  return useContext(QuotePageRuntimeContext);
}
